// File: typescript/apps/front-end/src/main/react/distinction/distinctionCopy.ts
import { Award, CalendarCheck, CircleCheckBig, Globe2, HandHeart, MessagesSquare, type LucideIcon } from "lucide-react";
import type { LocalizationKey } from "@/main/util/LocalizationUtil";

/**
 * The five distinctions (prestige badges) and their copy/icon map.
 *
 * Design: designs/00-inbox/2026-09-26-distinctions-prestige-badges-design.md §5, §6.1; copy and icons
 * are the work order's §2 (`designs/01-now/2026-09-26-distinctions-work-order.md`, D5), verbatim.
 *
 * Every catalog key is written out as a WHOLE LITERAL on purpose, never composed from the distinction
 * key at runtime: the catalog split derives which half a key ships in from literal references, and a
 * composed family would need an allowlist entry in `catalogPartition.ts` (AGENTS/localization.md).
 * Keep this module off the eager graph: only the lazily loaded page islands import it, so these keys
 * land in the deferred catalog rather than in the one preloaded on every page.
 *
 * The server already resolved WHICH distinction a byline shows (member pick, else the rarest held); the
 * order below is only the display order for lists (the profile row, the picker), rarest first, the same
 * fixed order the backend uses (A11).
 */
export const DISTINCTION_KEYS = [
    "PROBLEM_SOLVER",
    "CONVERSATION_STARTER",
    "WELCOMER",
    "REGULAR",
    "BRIDGE",
] as const;

export type DistinctionKey = (typeof DISTINCTION_KEYS)[number];

/** The `data-distinction` value for Voice of the month, which is not one of the five keys. */
export const VOICE_OF_MONTH_HOOK = "VOICE_OF_MONTH";

type DistinctionCopy = {
    nameKey: LocalizationKey;
    meaningKey: LocalizationKey;
    icon: LucideIcon;
};

const DISTINCTION_COPY: Record<DistinctionKey, DistinctionCopy> = {
    CONVERSATION_STARTER: {
        nameKey: "distinctions.CONVERSATION_STARTER.name",
        meaningKey: "distinctions.CONVERSATION_STARTER.meaning",
        icon: MessagesSquare,
    },
    WELCOMER: {
        nameKey: "distinctions.WELCOMER.name",
        meaningKey: "distinctions.WELCOMER.meaning",
        icon: HandHeart,
    },
    REGULAR: {
        nameKey: "distinctions.REGULAR.name",
        meaningKey: "distinctions.REGULAR.meaning",
        icon: CalendarCheck,
    },
    BRIDGE: {
        nameKey: "distinctions.BRIDGE.name",
        meaningKey: "distinctions.BRIDGE.meaning",
        icon: Globe2,
    },
    PROBLEM_SOLVER: {
        nameKey: "distinctions.PROBLEM_SOLVER.name",
        meaningKey: "distinctions.PROBLEM_SOLVER.meaning",
        icon: CircleCheckBig,
    },
};

export const VOICE_OF_MONTH_ICON: LucideIcon = Award;

export function isDistinctionKey(value: unknown): value is DistinctionKey {
    return typeof value === "string" && (DISTINCTION_KEYS as readonly string[]).includes(value);
}

/** `null` for anything that is not one of the five keys, so an unknown future key renders nothing. */
export function getDistinctionCopy(value: unknown): DistinctionCopy | null {
    return isDistinctionKey(value) ? DISTINCTION_COPY[value] : null;
}

/** Filters a server list down to known keys, deduplicated, in the fixed rarest-first order. */
export function sortDistinctionKeys(values: readonly unknown[]): DistinctionKey[] {
    const present = new Set(values.filter(isDistinctionKey));
    return DISTINCTION_KEYS.filter((key) => present.has(key));
}
